import { __assign, __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { forwardRef, useEffect, useRef, useState, useCallback, useMemo, } from "react";
import { ContentBody, ContentFooter, DatePickerContent, DatePickerTrigger, DatePickerWrapper, DayButton, MonthYearHeader, CalendarContainer, StyledCalendar, } from "./styles";
import { addMonths, eachDayOfInterval, endOfDay, endOfMonth, format, isAfter, isBefore, isSameDay, startOfMonth, subMonths, } from "date-fns";
import { Button } from "../Button";
import { fillDaysArray } from "../../utils/functions";
import { ptBR } from "date-fns/locale";
import { CalendarInputs } from "./CalendarComponents";
import { Spinner } from "../Spinner";
import { useTheme } from "styled-components";
import { IconArrowDropDown, IconCalendarCheck } from "../icons";
import { createPortal } from "react-dom";
import { FieldInfo } from "../FieldInfo";
var DayButtonMemo = React.memo(DayButton, function (prevProps, nextProps) {
    return (prevProps.$isSelected === nextProps.$isSelected &&
        prevProps.disabled === nextProps.disabled &&
        prevProps.children === nextProps.children);
});
export var SingleCalendar = forwardRef(function SingleCalendar(_a, ref) {
    var onlyDate = _a.onlyDate, onSelectDate = _a.onSelectDate, defaultDate = _a.defaultDate, isLoading = _a.isLoading, disabled = _a.disabled, minDate = _a.minDate, maxDate = _a.maxDate, value = _a.value, containerStyles = _a.containerStyles, label = _a.label, error = _a.error, props = __rest(_a, ["onlyDate", "onSelectDate", "defaultDate", "isLoading", "disabled", "minDate", "maxDate", "value", "containerStyles", "label", "error"]);
    var theme = useTheme();
    var triggerRef = useRef(null);
    var modalRef = useRef(null);
    var _b = useState(false), show = _b[0], setShow = _b[1];
    var _c = useState({
        top: "0px",
        left: "0px",
    }), position = _c[0], setPosition = _c[1];
    var defaultDateValue = useMemo(function () { return endOfDay(new Date()); }, []);
    var _d = useState(defaultDate || defaultDateValue), selectedDate = _d[0], setSelectedDate = _d[1];
    var _e = useState(defaultDate || defaultDateValue), dateOnButton = _e[0], setDateOnButton = _e[1];
    var _f = useState({
        start: startOfMonth(selectedDate),
        end: endOfMonth(selectedDate),
    }), monthToShow = _f[0], setMonthToShow = _f[1];
    var daysToShowMemo = useMemo(function () {
        var daysOfTheMonth = eachDayOfInterval(monthToShow);
        fillDaysArray(daysOfTheMonth);
        return daysOfTheMonth.map(function (day) { return ({
            value: day,
            day: day.getDate(),
            month: day.getMonth(),
            year: day.getFullYear(),
            weekday: day.getDay(),
        }); });
    }, [monthToShow]);
    var toggleShow = useCallback(function () {
        setSelectedDate(dateOnButton);
        setShow(function (state) { return !state; });
    }, [dateOnButton]);
    var onClickDay = useCallback(function (day) {
        if (onlyDate) {
            handleSelectDate(day);
            return;
        }
        setSelectedDate(endOfDay(day));
    }, [onlyDate]);
    var handleSelectDate = useCallback(function (date) {
        onSelectDate === null || onSelectDate === void 0 ? void 0 : onSelectDate(date);
        setDateOnButton(date);
        toggleShow();
    }, [onSelectDate, toggleShow]);
    var handleClickOutside = useCallback(function (event) {
        if (modalRef.current &&
            triggerRef.current &&
            !modalRef.current.contains(event.target) &&
            !triggerRef.current.contains(event.target)) {
            setShow(false);
        }
    }, []);
    var handlePreviousMonth = useCallback(function () {
        var newMonth = subMonths(monthToShow.start, 1);
        setMonthToShow({
            start: startOfMonth(newMonth),
            end: endOfMonth(newMonth),
        });
    }, [monthToShow.start]);
    var handleNextMonth = useCallback(function () {
        var newMonth = addMonths(monthToShow.start, 1);
        setMonthToShow({
            start: startOfMonth(newMonth),
            end: endOfMonth(newMonth),
        });
    }, [monthToShow.start]);
    useEffect(function () {
        if (value) {
            setDateOnButton(value);
            setSelectedDate(value);
        }
    }, [value]);
    useEffect(function () {
        if (show && modalRef.current && triggerRef.current) {
            var modalRect = modalRef.current.getBoundingClientRect();
            var triggerRect = triggerRef.current.getBoundingClientRect();
            var windowHeight = window.innerHeight;
            if (triggerRect.bottom + modalRect.height > windowHeight) {
                setPosition({
                    left: triggerRect.left + triggerRect.width + "px",
                    top: triggerRect.top - modalRect.height - 4 + "px",
                });
            }
            else {
                setPosition({
                    left: triggerRect.left + triggerRect.width + "px",
                    top: triggerRect.top + triggerRect.height + 4 + "px",
                });
            }
        }
        else {
            setPosition({
                top: "0px",
                left: "0px",
            });
        }
    }, [show]);
    useEffect(function () {
        window.addEventListener("click", handleClickOutside);
        return function () {
            window.removeEventListener("click", handleClickOutside);
        };
    }, [handleClickOutside]);
    useEffect(function () {
        setMonthToShow({
            start: startOfMonth(selectedDate),
            end: endOfMonth(selectedDate),
        });
    }, [selectedDate]);
    return (_jsxs(DatePickerWrapper, { style: containerStyles, ref: ref, children: [label && _jsx("label", { children: label }), _jsxs(DatePickerTrigger, __assign({ type: "button", ref: triggerRef, onClick: toggleShow, disabled: disabled || isLoading }, props, { children: [format(dateOnButton, "dd/MM/yyyy".concat(onlyDate ? "" : "- HH:mm")), isLoading ? (_jsx(Spinner, { size: 20, color: theme.colors.neutral.light })) : (_jsx(IconCalendarCheck, {}))] })), _jsx(FieldInfo, { isDisabled: disabled || isLoading, isError: !!error, children: error }), createPortal(_jsxs(DatePickerContent, { ref: modalRef, "$show": show, "$top": position.top, "$left": position.left, children: [_jsx(ContentBody, { children: _jsxs(CalendarContainer, { children: [_jsxs(MonthYearHeader, { children: [_jsx(IconArrowDropDown, { fontSize: 24, style: { transform: "rotate(90deg)" }, onClick: handlePreviousMonth }), _jsxs("span", { children: [format(monthToShow.start, "MMMM", { locale: ptBR }), ", ", format(monthToShow.start, "yyyy")] }), _jsx(IconArrowDropDown, { fontSize: 24, style: { transform: "rotate(-90deg)" }, onClick: handleNextMonth })] }), _jsxs("ul", { children: [_jsx("li", { children: "D" }), _jsx("li", { children: "S" }), _jsx("li", { children: "T" }), _jsx("li", { children: "Q" }), _jsx("li", { children: "Q" }), _jsx("li", { children: "S" }), _jsx("li", { children: "S" })] }), _jsx(StyledCalendar, { children: daysToShowMemo.map(function (day) { return (_jsx(DayButtonMemo, { onClick: function () { return onClickDay(day.value); }, "$isSelected": isSameDay(endOfDay(day.value), selectedDate), disabled: (minDate && isBefore(day.value, minDate)) ||
                                            (maxDate && isAfter(day.value, maxDate)), children: day.day }, day.value.toString())); }) }), !onlyDate && (_jsx(CalendarInputs, { selectedDate: selectedDate, setSelectedDate: setSelectedDate }))] }) }), !onlyDate && (_jsx(ContentFooter, { children: _jsx(Button, { variant: "outlined", onClick: function () { return handleSelectDate(selectedDate); }, children: "Aplicar" }) }))] }), document.body)] }));
});
//# sourceMappingURL=index.js.map